import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, LifeBuoy } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';

/**
 * Les messages de la page de connexion : identifiants refusés, serveur
 * injoignable, session déjà ouverte, déconnexion imposée, mot de passe changé.
 *
 * Même facture que les pop-ups de la page (PopupConnexion) : primitives Dialog,
 * en-tête à tuile, pied à boutons de même taille. Remplace l'ancien ErrorDialog,
 * dont le titre « Session active détectée » s'affichait aussi pour une
 * déconnexion, et dont le bouton support ouvrait une adresse e-mail en dur.
 * Le support ouvre désormais la pop-up réglée dans le CIK.
 *
 * Ancienne version, toujours utilisée par des pages hors service :
 * components/ErrorDialog.jsx
 */
const TYPES = {
  error: {
    Icone: AlertCircle,
    tuile: 'bg-red-50 text-red-600',
    titre: 'Connexion impossible'
  },
  warning: {
    Icone: AlertTriangle,
    tuile: 'bg-amber-50 text-amber-600',
    titre: 'À savoir avant de continuer'
  },
  success: {
    Icone: CheckCircle2,
    tuile: 'bg-emerald-50 text-emerald-600',
    titre: "C'est fait"
  }
};

// 44 px sur écran tactile (tablette, téléphone) ; à la souris, 36 px comme avant.
const bouton = 'h-9 min-w-[168px] justify-center gap-2 [@media(pointer:coarse)]:h-11';

const AlerteConnexion = ({ ouverte, type = 'error', titre, message, onClose, onSupport }) => {
  const { Icone, tuile, titre: titreParDefaut } = TYPES[type] || TYPES.error;

  return (
    <Dialog open={ouverte} onOpenChange={(ouvert) => { if (!ouvert) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-lg overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${tuile}`}>
            <Icone size={18} />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">{titre || titreParDefaut}</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">KLASYS</DialogDescription>
          </div>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">{message}</p>
        </div>

        <DialogFooter className="flex-col gap-2 border-t bg-gray-50 px-5 py-4 sm:flex-row sm:justify-end">
          {type === 'error' && onSupport && (
            <Button
              type="button"
              onClick={onSupport}
              className={`${bouton} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
            >
              <LifeBuoy size={15} />
              Contacter le support
            </Button>
          )}
          <Button
            type="button"
            onClick={onClose}
            className={`${bouton} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
          >
            {type === 'warning' ? 'Compris' : 'Fermer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AlerteConnexion;
